/**
 * AI-drafting badge/banner helpers — pure functions so the polling and
 * banner-copy decisions are unit-testable outside React.
 *
 * Backend contract: PortalEmailItem.ai_draft_state and
 * GET /portal/inbox/classification-activity (see app/routes/portal.py).
 */
import type { PortalClassificationActivity } from '../types/portal';

/** Poll cadence while an email is visibly being drafted by the AI. */
export const AI_DRAFT_POLL_MS = 15_000;

/**
 * Whether the inbox should silently re-poll: at least one visible email is
 * still drafting AND the user hasn't paginated past page one (a poll resets
 * the list to the first page, which would truncate a longer list).
 */
export function shouldPollForDrafting(
  emails: ReadonlyArray<{ ai_draft_state?: 'drafting' | 'failed' | null }>,
  loadedCount: number,
  pageSize: number,
): boolean {
  if (loadedCount > pageSize) return false;
  return emails.some((e) => e.ai_draft_state === 'drafting');
}

/**
 * Banner copy for the Order Review page. Null → no banner.
 * Failed emails take priority: they need action, drafting is informational.
 */
export function classificationActivityBanner(
  activity: PortalClassificationActivity | null,
): { kind: 'drafting' | 'failed'; text: string } | null {
  if (!activity) return null;
  if (activity.failed > 0) {
    const plural = activity.failed === 1 ? 'email' : 'emails';
    return {
      kind: 'failed',
      text:
        `AI processing failed on ${activity.failed} recent ${plural} — ` +
        'check the inbox and handle any orders manually.',
    };
  }
  if (activity.drafting > 0) {
    const plural = activity.drafting === 1 ? 'order draft' : 'order drafts';
    const age = formatDraftingAge(activity.oldest_drafting_seconds);
    return {
      kind: 'drafting',
      text:
        `AI is drafting ${activity.drafting} ${plural} from recent emails` +
        `${age ? ` (running ${age})` : ''} — they'll appear here automatically.`,
    };
  }
  return null;
}

/** "45s" / "3m" display for the longest-running drafting email. */
export function formatDraftingAge(seconds: number | null): string | null {
  if (seconds == null || seconds < 0) return null;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.floor(seconds / 60)}m`;
}
